import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FileText, 
  Edit, 
  Search, 
  Trash2,
  FileDown,
  FileText as DocxIcon,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { apiRequest } from '../utils/api';
import formatDocumentDisplayName from '../utils/documentDisplayName';
import { useNavigate } from 'react-router-dom';

const GeneratedDocumentsPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Document type configurations
  const documentTypes = {
    cv: { name: 'CV/Resume', icon: FileText, color: 'bg-blue-500' },
    sop: { name: 'Statement of Purpose', icon: FileText, color: 'bg-green-500' },
    contract: { name: 'Legal Contract', icon: FileText, color: 'bg-[var(--color-primary)]' }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('generation/list');
      if (response && response.generations) {
        setDocuments(response.generations);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (document) => {
    // Navigate to generate document page with prefilled data
    const queryParams = new URLSearchParams({
      edit: 'true',
      id: document.id,
      type: document.type
    });
    navigate(`/generate-document?${queryParams.toString()}`);
  };

  const handleDownload = async (document, format = 'pdf') => {
    try {
      if (document.download_url) {
        // Open the download URL in a new tab
        window.open(document.download_url, '_blank');
      } else {
        // If no download URL, try to regenerate the document
        setError('Download URL not available. Please regenerate the document.');
      }
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    const document = documents.find(doc => doc.id === documentId);
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      await apiRequest(`generation/${documentToDelete.id}`, {
        method: 'DELETE'
      });
      // Remove from local state
      setDocuments(prev => prev.filter(doc => doc.id !== documentToDelete.id));
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-[var(--color-success)]';
      case 'processing': return 'text-[var(--color-warning)]';
      case 'failed': return 'text-[var(--color-danger)]';
      default: return 'text-[var(--color-text-muted)]';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✓';
      case 'processing': return '⏳';
      case 'failed': return '✗';
      default: return '?';
    }
  };

  // Filter and sort documents
  const filteredAndSortedDocuments = documents
    .filter(doc => {
      const displayName = formatDocumentDisplayName(doc.filename, {
        documentType: doc.type,
        documentId: doc.id,
      });
      const matchesSearch = doc.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           doc.type?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterType === 'all' || doc.type === filterType;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'filename':
          aValue = formatDocumentDisplayName(a.filename, { documentType: a.type, documentId: a.id });
          bValue = formatDocumentDisplayName(b.filename, { documentType: b.type, documentId: b.id });
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        default:
          aValue = a[sortBy] || '';
          bValue = b[sortBy] || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <RefreshCw className="w-6 h-6 text-[var(--color-primary)] animate-spin" />
          <span className="text-[var(--color-text)]">Loading documents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Generated Documents – DocxsAi</title>
        <meta name="description" content="Your generated documents. This page is private and not indexed by search engines." />
        <link rel="canonical" href="https://docxsai.com/generated-documents" />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-2 whitespace-nowrap">
                Generated{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
                  Documents
                </span>
              </h1>
              <p className="text-[var(--color-text-muted)] text-sm sm:text-base">Manage and download your generated documents</p>
            </div>
            <button
              onClick={() => navigate('/generate-document')}
              className="brand-gradient-btn w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Generate New
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 text-[var(--color-danger)] px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Filters and Search */}
        <div className="elevated-card rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-muted)] w-4 h-4" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="themed-input w-full pl-10 pr-4 py-2 rounded-lg text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
              />
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="themed-input px-4 py-2 rounded-lg text-[var(--color-text)] focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="cv">CV/Resume</option>
              <option value="sop">Statement of Purpose</option>
              <option value="contract">Legal Contract</option>
            </select>

            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="themed-input px-4 py-2 rounded-lg text-[var(--color-text)] focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
            >
              <option value="created_at">Date Created</option>
              <option value="filename">Filename</option>
              <option value="type">Document Type</option>
            </select>

            {/* Sort Order */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="themed-input px-4 py-2 rounded-lg text-[var(--color-text)] focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>

        {/* Documents List */}
        <div className="elevated-card rounded-lg overflow-hidden">
          {filteredAndSortedDocuments.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-16 h-16 text-[var(--color-text-muted)] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[var(--color-text-secondary)] mb-2">No documents found</h3>
              <p className="text-[var(--color-text-muted)] mb-4">
                {searchTerm || filterType !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Start by generating your first document'
                }
              </p>
              {!searchTerm && filterType === 'all' && (
                <button
                  onClick={() => navigate('/generate-document')}
                  className="brand-gradient-btn px-4 py-2 rounded-lg"
                >
                  Generate Document
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--color-surface-muted)]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
                  {filteredAndSortedDocuments.map((document) => (
                    <tr key={document.id} className="hover:bg-[var(--color-surface-muted)] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${documentTypes[document.type]?.color || 'bg-[var(--color-text-muted)]'}`}>
                            <FileText className="w-4 h-4 text-white" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-[var(--color-text)]" title={document.filename}>
                              {formatDocumentDisplayName(document.filename, {
                                documentType: document.type,
                                documentId: document.id,
                              })}
                            </div>
                            <div className="text-sm text-[var(--color-text-muted)]">
                              ID: {document.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${documentTypes[document.type]?.color || 'bg-[var(--color-text-muted)]'} text-white`}>
                          {documentTypes[document.type]?.name || document.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center text-sm ${getStatusColor(document.status)}`}>
                          <span className="mr-1">{getStatusIcon(document.status)}</span>
                          {document.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
                        {formatDate(document.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {/* Edit Button */}
                          <button
                            onClick={() => handleEdit(document)}
                            className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] p-1 rounded hover:bg-[var(--color-surface-muted)] transition-colors"
                            title="Edit and regenerate"
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          {/* PDF Download */}
                          <button
                            onClick={() => handleDownload(document, 'pdf')}
                            className="text-[var(--color-danger)] hover:opacity-80 p-1 rounded hover:bg-[var(--color-surface-muted)] transition-colors"
                            title="Download PDF"
                            disabled={document.status !== 'completed'}
                          >
                            <FileDown className="w-4 h-4" />
                          </button>

                          {/* DOCX Download */}
                          <button
                            onClick={() => handleDownload(document, 'docx')}
                            className="text-[var(--color-success)] hover:opacity-80 p-1 rounded hover:bg-[var(--color-surface-muted)] transition-colors"
                            title="Download DOCX"
                            disabled={document.status !== 'completed'}
                          >
                            <DocxIcon className="w-4 h-4" />
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDelete(document.id)}
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)] p-1 rounded hover:bg-[var(--color-surface-muted)] transition-colors"
                            title="Delete document"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-6 text-sm text-[var(--color-text-muted)]">
          Showing {filteredAndSortedDocuments.length} of {documents.length} documents
        </div>
      </div>

      {/* Custom Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-overlay)] backdrop-blur-sm">
          <div className="elevated-card rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--color-text)]">Delete Document</h3>
            </div>
            
            <p className="text-[var(--color-text-secondary)] mb-6">
              Are you sure you want to delete "{formatDocumentDisplayName(documentToDelete?.filename, { documentType: documentToDelete?.type, documentId: documentToDelete?.id }) || 'this document'}"? 
              This action cannot be undone and will remove the file from both the database and storage.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  if (isDeleting) return;
                  setShowDeleteModal(false);
                  setDocumentToDelete(null);
                }}
                className="px-4 py-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-muted)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-[var(--color-danger)] text-[var(--color-text-inverse)] rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedDocumentsPage;
